import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { TextInput, RadioInput, CheckboxInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs'
import { FormProvider, useForm } from 'react-hook-form';
import BackNext from '../../Common/SubComponents/BackNext';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY } from '../../../../sessionStorage';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import { InputMaskZip, InputMaskDate, InputMaskPhone } from '../../../Helpers/Formatters';
import { customValidators, object, string, mixed } from '../../../Helpers/Validators/customValidators'
import CalculatePolicyEffectiveDate from '../../../Helpers/Dates/Americo/PolicyDate';
import { Dialog, DialogContent, IconButton, Button, CircularProgress } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Step10Icon from '../icons/step_10.svg?react';
import IconHolder from '../../../Helpers/IconHolder';
import { savedQuoteEmailDrip } from '../../Common/Helpers/SavedQuoteEmailDrip';
import { yupResolver } from '@hookform/resolvers/yup';

export default function FinancialInformation(props) {

  const [loading, setLoading] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);

  const [routingLoading, setRoutingLoading] = useState('');
  const [hasRoutingNumberError, setHasRoutingNumberError] = useState(false);
  const [financialInstitution, setFinancialInstitution] = useState('');
  const routingErrorMessage = useRef();

  const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;
  const formMethods = useForm({
    resolver: yupResolver(FinancialInformation.ValidationSchema(props.ageSpecs, props.fullFormState.steps.basicInfo.birthdate)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, trigger } = formMethods;

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const onNext = () => {

    if (hasRoutingNumberError) {
      setTimeout(() => {
        if (routingErrorMessage.current) {
          window.scrollTo({
            top: routingErrorMessage.current.offsetTop,
            behavior: "smooth"
          });
        }
      }, 100);
      return;
    }

    setLoading(true);

    fetch(`${covrServicesUrl}/api/Americo/CreateInitialCase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
      },
      body: JSON.stringify(props.fullFormState) //test formstate being updated prior with payment info
    })
      .then(res => res.json())
      .then(json => {
        if (json.appIdentifier) {
          let draftDate;
          if (props.formState.draftPreference === 'Specific') {
            draftDate = new Date(props.formState.specificStartDate);
          }
          else {
            draftDate = new Date();
          }

          props.handlePaymentComplete(json.appIdentifier, CalculatePolicyEffectiveDate(draftDate));
          savedQuoteEmailDrip(props.fullFormState.steps.basicInfo.email);
        }
        else {
          setLoading(false)
        }
      })
  }

  const routingNumberCheck = () => {
    setFinancialInstitution('');
    setHasRoutingNumberError(false);

    const regex = RegExp(/^[0-9]{9}$/);

    const isValidRouting = regex.test(props.formState.routingNumber);

    if (isValidRouting) {
      setRoutingLoading(<CircularProgress size={25} thickness={4.5} />);

      fetch(`${covrServicesUrl}/api/Americo/GetBankNameFromRoutingNumber?routingNumber=${props.formState.routingNumber}`)
        .then(res => res.json())
        .then(json => {
          if (json.success) {
            setFinancialInstitution(json.bankName)
            setRoutingLoading('');
          }
          else if (!json.success) {
            setHasRoutingNumberError(true);
            setRoutingLoading('');
          }
        })
    }
  }

  // app id must be set before proceeding to esign
  useEffect(() => {
    if (props.fullFormState.appIdentifier && loading) {
      props.onNext();
    }
  }, [props.fullFormState.appIdentifier]);

  useEffect(() => {
    handleValueChange('institutionName', financialInstitution)
  }, [financialInstitution]);

  let debounce = null;
  useEffect(() => {
    let isSubscribed = true;
    if (debounce) {
      clearTimeout(debounce);
    }

    debounce = setTimeout(() => routingNumberCheck(() => isSubscribed), 1000);
    return () => {
      clearTimeout(debounce);
      isSubscribed = false;
    }
  }, [props.formState.routingNumber])

  return (
    <Container>
      <FormProvider {...formMethods}>
        <Form className="quote-form" onSubmit={handleSubmit(onNext)}>
          <Row className="formPage justify-content-center">
            <Col lg={6} className='w-100'>
              <Row className="justify-content-center">
                <IconHolder icon={Step10Icon} viewBox="0 0 25 25" color='primary' />
              </Row>
              <h4 className="headerTitle">Securing Your Coverage Today</h4>
              <h5 className="headerSubtitle">You'll receive your policy after payment and signatures are received.</h5>

              <div className='mb-3'>
                <SelectInput
                  id="payor"
                  name="payor"
                  value={props.formState.payor}
                  onChange={e => handleValueChange('payor', e.target.value)}
                  label="Who is paying?"
                  selectOptions={[{ value: "Self", label: "Self" }, { value: "Company", label: "Company" }]}
                />
              </div>

              {props.formState.payor === "Company" &&
                <React.Fragment>
                  <div className='mb-3'>
                    <StyledRadioInput
                      name="signingAuthority"
                      id="signingAuthority"
                      label="The proposed insured has signing authority for the company selected as Payor?"
                      value={props.formState.signingAuthority}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('signingAuthority', e)}
                    />

                  </div>

                  <div className='mb-3'>
                    <TextInput
                      name="companyName"
                      id="companyName"
                      label="Company Name"
                      value={props.formState.companyName}
                      onChange={(e) => props.onFieldChange('companyName', e.target.value)}
                    />
                  </div>

                  <div className='mb-3'>
                    <TextInput
                      name="payorMailingStreet"
                      id="payorMailingStreet"
                      label="Mailing Address"
                      value={props.formState.payorMailingStreet}
                      onChange={e => props.onFieldChange('payorMailingStreet', e.target.value)}
                    />
                  </div>

                  <div className='mb-3'>
                    <TextInput
                      name="payorMailingCity"
                      id="payorMailingCity"
                      label="City"
                      value={props.formState.payorMailingCity}
                      onChange={e => props.onFieldChange('payorMailingCity', e.target.value)}
                    />
                  </div>

                  <Row>
                    <Col>
                      <div className='mb-3'>
                        <SelectInput
                          name="payorMailingState"
                          id="payorMailingState"
                          value={props.formState.payorMailingState}
                          onChange={(e) => handleValueChange('payorMailingState', e.target.value)}
                          label="State"
                          selectOptions={StatesWithDisabled(["NY"])}
                        />
                      </div>
                    </Col>
                    <Col>
                      <div className='mb-3'>
                        <TextInput
                          name="payorMailingZip"
                          id="payorMailingZip"
                          label="Zip"
                          value={props.formState.payorMailingZip}
                          onChange={e => props.onFieldChange('payorMailingZip', e.target.value)}
                          InputProps={{
                            inputComponent: InputMaskZip,
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <div className='mb-3'>
                    <StyledRadioInput
                      name="mailingAdressISPOBox"
                      id="mailingAdressISPOBox"
                      label="Is mailing address a PO Box?"
                      value={props.formState.mailingAdressISPOBox}
                      options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                      onChange={e => handleValueChange('mailingAdressISPOBox', e)}
                    />
                  </div>

                  {(props.formState.mailingAdressISPOBox === "true") &&

                    <React.Fragment>
                      <div className='mb-3'>
                        <TextInput
                          name="payorStreetAdress"
                          id="payorStreetAdress"
                          label="Street Address"
                          value={props.formState.payorStreetAdress}
                          onChange={e => props.onFieldChange('payorStreetAdress', e.target.value)}
                        />
                      </div>

                      <div className='mb-3'>
                        <TextInput
                          name="payorStreetCity"
                          id="payorStreetCity"
                          label="City"
                          value={props.formState.payorStreetCity}
                          onChange={e => props.onFieldChange('payorStreetCity', e.target.value)}
                        />
                      </div>

                      <Row>
                        <Col>
                          <SelectInput
                            name="payorStreetState"
                            id="payorStreetState"
                            value={props.formState.payorStreetState}
                            onChange={(e) => handleValueChange('payorStreetState', e.target.value)}
                            label="State"
                            selectOptions={StatesWithDisabled(["NY"])}
                          />
                        </Col>
                        <Col>
                          <div className='mb-3'>
                            <TextInput
                              name="payorStreetZip"
                              id="payorStreetZip"
                              label="Zip"
                              value={props.formState.payorStreetZip}
                              onChange={e => props.onFieldChange('payorStreetZip', e.target.value)}
                              InputProps={{
                                inputComponent: InputMaskZip,
                              }}
                            />
                          </div>
                        </Col>
                      </Row>

                    </React.Fragment>
                  }
                </React.Fragment>
              }

              {props.formState.payor === "Company" && (
                <div className='mb-3'>
                  <TextInput
                    name="payorPhoneNumber"
                    id="payorPhoneNumber"
                    label="Phone Number"
                    value={props.formState.payorPhoneNumber}
                    onChange={(e) => handleValueChange('payorPhoneNumber', e.target.value)}
                    InputProps={{
                      inputComponent: InputMaskPhone,
                    }}
                  />
                </div>
              )}

              <div className='mb-3'>
                <TextInput
                  name="routingNumber"
                  id="routingNumber"
                  label="Routing Number"
                  value={props.formState.routingNumber}
                  onChange={(e) => handleValueChange('routingNumber', e.target.value)}
                />
              </div>

              {
                hasRoutingNumberError && (
                  <div ref={routingErrorMessage} className="text-danger text-center mb-3">Unable to verify routing number.</div>)
              }

              <div className="mb-1">
                <TextInput
                  name="accountNumber"
                  id="accountNumber"
                  label="Account Number"
                  value={props.formState.accountNumber}
                  onChange={(e) => handleValueChange('accountNumber', e.target.value)}
                />
              </div>

              <Row className="justify-content-center pb-3 pt-3">
                <Button type="button" className="btn btn-link" onClick={() => { setHelpDialogOpen(true) }} color="primary"><h6 className="mb-0">WHERE TO FIND THIS? </h6></Button>
              </Row>

              <div className='mb-3'>
                <TextInput
                  name="institutionName"
                  id="institutionName"
                  label="Institution Name"
                  value={props.formState.institutionName}
                  disabled={true}
                  onChange={(e) => handleValueChange('institutionName', e.target.value)}
                  InputProps={{
                    endAdornment: routingLoading
                  }}
                />
              </div>

              <div className='mb-3'>
                <SelectInput
                  id="accountType"
                  name="accountType"
                  value={props.formState.accountType}
                  onChange={e => handleValueChange('accountType', e.target.value)}
                  label="Account Type"
                  selectOptions={[{ value: "Savings Account", label: "Savings Account" }, { value: "Checking Account", label: "Checking Account" }]}
                />
              </div>

              <div className='mb-3'>
                <StyledRadioInput
                  name="businessAccount"
                  id="businessAccount"
                  label="Is this a Business Account?"
                  value={props.formState.businessAccount}
                  options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                  onChange={e => handleValueChange('businessAccount', e)}
                />
              </div>

              <div className='mb-3'>
                <TextInput
                  name="nameOnAccount"
                  id="nameOnAccount"
                  label="Name on Account"
                  value={props.formState.nameOnAccount}
                  onChange={(e) => handleValueChange('nameOnAccount', e.target.value)}
                />
              </div>

              <div className='mb-3'>
                <CheckboxInput
                  name="paymentAgree"
                  id="paymentAgree"
                  checked={ConvertStringToBoolean(props.formState.paymentAgree)}
                  onChange={e => handleValueChange('paymentAgree', ConvertBooleanToString(e.target.checked))}
                  isValueString
                  label="I agree to make payments from this account to cover the cost of this insurance."
                />
              </div>

              <div className='mb-3'>
                <RadioInput
                  name="draftPreference"
                  id="draftPreference"
                  label="What is your payment preference?"
                  value={props.formState.draftPreference}
                  radios={[{ value: "Today", label: "Pay and start coverage today" },
                  { value: "Specific", label: "Specific start date" }]}
                  onChange={e => handleValueChange('draftPreference', e.target.value)}
                />
              </div>

              {props.formState.draftPreference === "Specific" &&
                <div className='mb-3'>
                  <TextInput
                    name="specificStartDate"
                    id="specificStartDate"
                    label="Start Date"
                    value={props.formState.specificStartDate}
                    inputRef={props.register}
                    onChange={(e) => handleValueChange('specificStartDate', e.target.value)}
                    InputProps={{
                      inputComponent: InputMaskDate,
                    }}
                  />
                </div>
              }
            </Col>
          </Row>
          <BackNext onBack={props.onBack} disabled={loading || (routingLoading !== '')} loading={loading} colSize={2} width={100} continueLabel={{ text: 'Next' }} />
        </Form>
      </FormProvider>
      <HelpDialog
        open={helpDialogOpen}
        onHelpDialogClosed={() => {
          setHelpDialogOpen(false);
        }}
      />
    </Container>
  )
}

function HelpDialog(props) {
  return (
    <div>
      <Dialog maxWidth="sm" className="help-dialog" open={props.open} onClose={props.onHelpDialogClosed} scroll="body">
        <DialogContent className="product-info-dialog-content pt-1">
          <IconButton
            className="product-info-close-button"
            aria-label="close"
            onClick={props.onHelpDialogClosed}
            size="large">
            <CloseIcon />
          </IconButton>
          <br />
          <Row className="justify-content-center image-check">
            <img className='px-0' src={`${process.env.PUBLIC_URL}/americo/check.png`} alt="Check" />
          </Row>
        </DialogContent>
      </Dialog>
    </div>
  );
}

FinancialInformation.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

FinancialInformation.ValidationSchema = (ageSpecs, piBirthdate) => {
  return object().shape({
    payor: string().required('Payor is required.'),
    signingAuthority: string().when('payor', {
      is: "Company",
      then: (schema) => schema.required('This field is required.').matches(/^true/, 'The insured must have signing authority to name Company as payor.'),
      otherwise: (schema) => schema.notRequired()
    }),
    companyName: string().when('payor', {
      is: "Company",
      then: (schema) => schema.required('Company Name is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    payorMailingStreet: customValidators.string().when('payor', {
      is: "Company",
      then: (schema) => schema.isValidAddress('Invalid address. No special character is allowed').required('Mailing Address is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    payorMailingCity: string().when('payor', {
      is: "Company",
      then: (schema) => schema.required('City is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    payorMailingState: string().when('payor', {
      is: "Company",
      then: (schema) => schema.required('State is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    payorMailingZip: customValidators.string().when('payor', {
      is: "Company",
      then: (schema) => schema.isValidNumbersOnly('Invalid zip', 5).required('Zip is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    mailingAdressISPOBox: string().when('payor', {
      is: "Company",
      then: (schema) => schema.required('This field is required.'),
      otherwise: (schema) => schema.notRequired()
    }),
    payorStreetAdress: customValidators.string().when(['mailingAdressISPOBox', 'payor'], ([mailingAdressISPOBox, payor], schema) => {
      return mailingAdressISPOBox === "true" && payor === "Company"
        ? schema.isValidAddress('Invalid address. No special character is allowed').required('Street Adress is required.')
        : string().notRequired();
    }),
    payorStreetCity: string().when(['mailingAdressISPOBox', 'payor'], ([mailingAdressISPOBox, payor], schema) => {
      return mailingAdressISPOBox === "true" && payor === "Company"
        ? schema.required('City is required.')
        : string().notRequired();
    }),
    payorStreetState: string().when(['mailingAdressISPOBox', 'payor'], ([mailingAdressISPOBox, payor], schema) => {
      return mailingAdressISPOBox === "true" && payor === "Company"
        ? schema.required('State is required.')
        : string().notRequired();
    }),
    payorStreetZip: customValidators.string().when(['mailingAdressISPOBox', 'payor'], ([mailingAdressISPOBox, payor], schema) => {
      return mailingAdressISPOBox === "true" && payor === "Company"
        ? schema.isValidNumbersOnly('Invalid zip', 5).required('Zip is required.')
        : string().notRequired();
    }),
    routingNumber: string()
      .required('Routing Number is required.')
      .matches(/^[0-9]{9}$/, 'The routing number must be 9 digits in length.'),
    accountNumber: string()
      .required('Account Number is required.')
      .matches(/^[0-9]{4,17}$/, 'The account number must be between 4 and 17 digits in length.'),
    accountType: string().required('Account Type is required.'),
    businessAccount: string().required('This field is required.'),
    nameOnAccount: string().notRequired(),
    paymentAgree: mixed().oneOf(["true"], 'You must agree to the payment authorization.'),
    draftPreference: string().required('This field is required.'),
    specificStartDate: customValidators.string().when('draftPreference', {
      is: "Specific",
      then: (schema) => schema.required('Specific start date is required.').isValidSpecificStartDate('Start date must be within 30 days and cannot be on the 29th, 30th, or 31st of the month.', piBirthdate),
      otherwise: (schema) => schema.notRequired()
    }),
    payorPhoneNumber: customValidators.string().when('payor', {
      is: "Company",
      then: (schema) => schema
        .required('Phone Number is required.')
        .isValidPhone('Phone Number must be formatted as "(123) 456-7890".'),
      otherwise: (schema) => schema.notRequired()
    }),
  });
};

FinancialInformation.DefaultFormState = (prefill = false) => {
  return {
    payor: prefill ? 'Company' : '',
    signingAuthority: prefill ? 'true' : '',
    companyName: prefill ? 'Covr' : '',
    payorMailingStreet: prefill ? "2020 Covr St" : "",
    payorMailingCity: prefill ? "Boise" : "",
    payorMailingState: prefill ? "MA" : "",
    payorMailingZip: prefill ? "83702" : "",
    mailingAdressISPOBox: prefill ? "false" : "",
    payorStreetAdress: prefill ? "2020 Covr St" : "",
    payorStreetCity: prefill ? "Boise" : "",
    payorStreetState: prefill ? "MA" : "",
    payorStreetZip: prefill ? "83702" : "",
    lengthOfTimeAtCurrentAddress: "",
    payorPriorMailingStreet: "",
    payorPriorMailingCity: "",
    payorPriorMailingState: "",
    payorPriorMailingZip: "",
    routingNumber: prefill ? '081218879' : '',
    accountNumber: prefill ? '11111111112' : '',
    institutionName: prefill ? '' : '',
    accountType: prefill ? 'Savings Account' : '',
    businessAccount: prefill ? 'true' : '',
    nameOnAccount: prefill ? 'Props' : '',
    paymentAgree: prefill ? 'true' : 'false',
    draftPreference: prefill ? 'Specific' : 'Pay and start coverage today',
    specificStartDate: CalculatePolicyEffectiveDate(new Date()),
    payorPhoneNumber: prefill ? "(123) 345-4567" : "",
  };
};